import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { AppProviders, LocaleBoundary } from '@/app/providers'
import { wireAuth } from '@/app/providers/auth-bridge'
import { router } from '@/app/router/router'

import './index.css'

// Wired before the first render, so a 401 during the very first request
// already has somewhere to go.
wireAuth({
  onSessionLost: () => void router.navigate({ to: '/login', replace: true }),
})

// Names whatever moves, in the console, while you click around. Layout shift
// depends on the browser drawing the page — scrollbar width above all — so it
// cannot be caught in a headless run; the check has to live where the problem
// does. `import.meta.env.DEV` is statically false in a production build, so the
// import and the module behind it are dropped rather than shipped.
if (import.meta.env.DEV) {
  void import('@/app/dev/layout-shift-reporter').then((m) => {
    m.reportLayoutShifts()
  })
}

const rootElement = document.getElementById('root')
if (rootElement === null) throw new Error('#root is missing from index.html')

/**
 * The production build loads the stylesheet as `media="print"` so it stops
 * blocking the boot shell — see `build/head-plugin.ts`. That trades a
 * render-blocking request for a flash of unstyled content, and this is where
 * the trade is bought back: React does not mount until the sheet has applied,
 * so nothing styled by it can ever paint without it.
 *
 * In practice the wait is nothing. Measured on a throttled profile the sheet
 * lands at 1121ms and this file executes past 2400ms — it has always already
 * arrived. In development Vite injects styles through JS and there is no link
 * at all, so this resolves immediately.
 */
function whenStylesApplied(): Promise<void> {
  const link = document.querySelector<HTMLLinkElement>('link[data-css]')
  if (link === null || link.sheet !== null) return Promise.resolve()

  return new Promise<void>((resolve) => {
    const done = () => {
      resolve()
    }
    link.addEventListener('load', done, { once: true })
    // A stylesheet that failed is not a reason to never mount: an unstyled
    // panel beats a blank page, and the error is already in the console.
    link.addEventListener('error', done, { once: true })
  })
}

void whenStylesApplied().then(() => {
  // Replaces the static boot shell that has already painted.
  createRoot(rootElement).render(
    <StrictMode>
      <AppProviders>
        <LocaleBoundary>
          <RouterProvider router={router} />
        </LocaleBoundary>
      </AppProviders>
    </StrictMode>,
  )
})
